<template>
  <nav class="flex h-10 flex-row items-center justify-between">
    <div class="flex flex-row items-center">
      <NuxtLink :to="localePath('/')">
        <NuxtImg src="/images/logos/logo-black-vertical.svg" class="h-10" />
      </NuxtLink>
      <ul class="flex flex-row items-center gap-8 ml-[50px]">
        <li v-for="item in navigationItems" :key="item.key">
          <NuxtLink
            :to="localePath(item.to)"
            class="flex flex-row items-center gap-[11px] transition-colors duration-200 text-[#0D0F15]"
          >
            <UIIcon :name="item.icon" class="w-5 h-5" />
            <span 
              class="text-[14px] leading-[100%] tracking-[0%] align-middle whitespace-nowrap"
              :class="activeKey === item.key ? 'font-bold' : 'font-normal'"
            >{{ item.label }}</span>
          </NuxtLink>
        </li>
      </ul>
    </div>
    <div class="flex flex-row items-center gap-4">
      <PortfolioSearch
        v-if="isPortfolioPage"
        container-class="w-[300px]"
      />
      <LanguageSelector />
      <div
        v-if="authStore.user"
        ref="activator"
        class="flex cursor-pointer flex-row items-center gap-4"
        @click="isMenuVisible = !isMenuVisible"
      >
        <UIAvatar
          :src="authStore.user?.profileImageUrl"
          :user="authStore.user || undefined"
          :height="40"
          :width="40"
          no-border
          class="!text-lg"
        />

        <div class="flex flex-row items-center gap-2">
          <span class="font-bold text-black-90 lg:hidden [1150px]:block"> {{ authStore.user?.firstName }} {{ authStore.user?.lastName }} </span>
          <UIIcon
            name="chevron-big-filled-down"
            class="text-gold-primary-120 transition-all duration-200"
            :class="{
              'rotate-180': isMenuVisible,
            }"
          />
        </div>
      </div>
      <UIMenu
        v-if="isMenuVisible && authStore.user"
        ref="menu"
        value-key="id"
        :menu-visible="isMenuVisible"
        :style="floatingStyles"
        custom-content
        menu-width="250px"
      >
        <template #custom>
          <div class="flex flex-col py-2 text-black-70">
            <div class="flex flex-col px-4 py-2">
              <h3 class="font-bold text-black-80">{{ authStore.user?.firstName }} {{ authStore.user?.lastName }}</h3>
              <span class="text-xs text-black-40">{{ authStore.user?.email }}</span>
            </div>
            <NuxtLink
              v-if="authStore.getIsAdmin"
              :to="localePath('/')"
              class="flex h-10 cursor-pointer flex-row items-center gap-2 px-4 py-2 transition-colors duration-200 hover:bg-black-03"
              @click.capture="isMenuVisible = false"
            >
              <UIIcon name="home" />
              {{ $t('navigation.header.home') }}
            </NuxtLink>
            <NuxtLink
              :to="localePath('/profile#personal')"
              class="flex h-10 cursor-pointer flex-row items-center gap-2 px-4 py-2 transition-colors duration-200 hover:bg-black-03"
              @click.capture="isMenuVisible = false"
            >
              <UIIcon name="user" />
              {{ $t('navigation.header.profile') }}
            </NuxtLink>
            <NuxtLink
              :to="localePath('/profile#settings')"
              class="flex h-10 cursor-pointer flex-row items-center gap-2 px-4 py-2 transition-colors duration-200 hover:bg-black-03"
              @click.capture="isMenuVisible = false"
            >
              <UIIcon name="settings" />
              {{ $t('navigation.header.settings') }}
            </NuxtLink>
            <NuxtLink
              v-if="authStore.getIsAdmin"
              :to="localePath('/admin/users')"
              class="flex h-10 cursor-pointer flex-row items-center gap-2 px-4 py-2 transition-colors duration-200 hover:bg-black-03"
              @click.capture="isMenuVisible = false"
            >
              <UIIcon name="id" />
              {{ $t('navigation.header.admin') }}
            </NuxtLink>
            <hr class="my-1 w-full border-black-10" />
            <button
              class="flex h-10 cursor-pointer flex-row items-center gap-2 px-4 py-2 transition-colors duration-200 hover:bg-black-03"
              @click="logout()"
            >
              <UIIcon name="log-out" />
              {{ $t('navigation.header.logout') }}
            </button>
          </div>
        </template>
      </UIMenu>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { autoUpdate, flip, offset, useFloating } from '@floating-ui/vue'
import { useTolgee } from '@tolgee/vue'

const { mutateAsync: logout } = useLogout()

const authStore = useAuthStore()
const route = useRoute()
const localePath = useLocalePath()
const tolgee = useTolgee()
const uiStore = useUIStore()

type NavigationItem = {
  key: 'services' | 'prelaunch' | 'explore' | 'portfolio' | 'training'
  label: string
  to: string
  icon: string
}

const navigationItems = computed<NavigationItem[]>(() => [
  { key: 'services', label: tolgee.value.t('services.title'), to: '/services', icon: 'services' },
  { key: 'prelaunch', label: tolgee.value.t('prelaunch.badge.prelaunch'), to: '/?list=1', icon: 'prelaunch' },
  { key: 'explore', label: tolgee.value.t('navigation.drawer.explore'), to: '/explore', icon: 'explore' },
  { key: 'portfolio', label: tolgee.value.t('portfolio.tabs.title'), to: '/portfolio', icon: 'portfolio' },
  { key: 'training', label: tolgee.value.t('training.title'), to: '/training', icon: 'training' },
])

const paths = computed(() => ({
  home: localePath('/'),
  explore: localePath('/explore'),
  profile: localePath('/portfolio'),
  services: localePath('/services'),
  training: localePath('/training'),
}))

const isSidePanelOpen = computed(() => uiStore.getDynamicSidePanel.value)

const activeKey = computed<NavigationItem['key'] | null>(() => {
  if (isSidePanelOpen.value) {
    return null
  }

  const routeName = route.name?.toString() || ''
  const fromQuery = route.query?.from
  const p = route.path

  if (p.startsWith(paths.value.services)) {
    return 'services'
  }

  if (p.startsWith(paths.value.training)) {
    return 'training'
  }

  if (p.startsWith(paths.value.profile)) {
    return route.hash?.includes('training') ? 'training' : 'portfolio'
  }

  if (p.includes('/add-property') || p.includes('/property/')) {
    return 'portfolio'
  }

  if (fromQuery === 'portfolio') {
    return 'portfolio'
  }

  if (routeName.includes('properties-id') || p.includes('/properties/')) {
    const fromQuery = route.query?.from
    if (fromQuery === 'explore') {
      return 'explore'
    }
    return 'prelaunch'
  }

  if (p === paths.value.explore || p === `${paths.value.explore}/`) {
    return 'explore'
  }

  if (p === paths.value.home || p === `${paths.value.home}/`) {
    const hasListQuery = route.query?.list
    const isListView = hasListQuery === '1' || hasListQuery === 'true'
    if (isListView) return 'prelaunch'
  }

  return null
})

const isPortfolioPage = computed(() => {
  const path = route.path || ''
  return path === '/portfolio' || path.endsWith('/portfolio') || route.name?.toString()?.includes('portfolio')
})

const menu = ref(null)
const activator = ref<HTMLDivElement | null>(null)

const isMenuVisible = ref(false)

const { floatingStyles } = useFloating(activator, menu, {
  placement: 'bottom-end',
  strategy: 'fixed',
  whileElementsMounted: autoUpdate,
  middleware: [
    flip({
      fallbackPlacements: ['top-end'],
    }),
    offset(5),
  ],
  open: isMenuVisible,
})
onClickOutside(
  menu,
  () => {
    isMenuVisible.value = false
  },
  { ignore: [activator] }
)
</script>

<style scoped></style>
